/* eslint-disable @typescript-eslint/naming-convention */
import {
  IStates,
  IPayPalSettings,
  CONVENIENCE_STORE,
  IPhoneCountryPattern,
  IExternalRoutes,
} from '../domain/entities/Config';
import { UNIT } from '../domain/entities/Store/Store';
import SHIPPING_METHODS from '../constants/ShippingMethods';
import { IAddressSchemaProperties } from './JsonSchemaApi';
import { ADDRESS_TYPE_ID, BACKEND_VALIDATION_TYPES } from '../constants/Constants';
import { SKU_TYPE } from '../domain/entities/Cart/CartItem';
import { IApiErrorElement } from '.';
import { IAddressAttributes } from '../domain/entities/Address';
import { IDeliveryOption } from '../domain/entities/Transaction/Transaction';
import { RETAIL_FEE_REGION } from '../constants/RetailDeliveryFeeMappedTranslations';

export interface IAddressSuggestionResponse {
  address1: string;
  city: string;
  state: string;
  postal_code: string;
  zipcode?: string;
}

export interface IUPSValidation {
  suggestions: IAddressSuggestionResponse[];
}

export interface IAddressResponse {
  fifthLineAddress?: string;
  fourthLineAddress?: string;
  thirdLineAddress?: string;
  secondLineAddress?: string;
  firstLineAddress?: string;
  first_name?: string;
  last_name?: string;
  first_name_alternate?: string;
  last_name_alternate?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  address5?: string;
  city?: string;
  colony?: string;
  municipality?: string;
  state?: string;
  country_id?: number;
  country?: ICountry;
  country_label?: ICountry; // TODO: This property is repeated with country and not used, remove from API when we can
  postal_code?: string;
  phone1?: string;
  phone1_type?: number;
  phone2?: string;
  phone2_type?: number;
  default_shipping?: number;
  company_name?: string;
  address_id?: number;
  created_date?: string;
  updated_date?: string;
  delivery_instructions?: string;
  bad_fields?: string[];
  address_type?: ADDRESS_TYPE_ID;
  abbr?: string,
  label?: string,
  value?: number;
  ship_type?: number;
}

export interface IGenericAddressOptionsResponse {
  [key: string]: string[];
}

export interface IOrderSummaryResponse {
  total_cost: number;
  total_shipping_applied_price: number;
  total_applied_tax: number;
  formatted_tax_rate: string;
  tax_rate: number;
  total_items_base_price: number;
  currency_code: string;
  total_items_discount: number;
  giftWrap: number;
  total_giftwrap_applied_price: number;
  items_count: number;
  gift_card_payment_amount: number;
  subtotal_without_donations: number;
  total_donations: number;
  total_included_tax: number;
  tax_is_estimated: number;
  rewardPointsPriceInCartFormatted: string;
  total_retail_delivery_fee: number;
  cod_surcharge_amount: number;
  estimated_delivery_start_date?: string;
  estimated_delivery_end_date?: string;
  retail_delivery_fee_message?: RETAIL_FEE_REGION;
}

export interface IShippingMethodResponse {
  discount: number;
  sort_order: number;
  name: string;
  price: number;
  sku_base_id: number;
  ship_method_id: SHIPPING_METHODS;
  country_ids?: string[];
  lead_time_value?: number;
  next_available_sat?: string;
  estimated_delivery_start_date?: string;
  estimated_delivery_end_date?: string;
  available_window?: {
    start_hour: number,
    stop_hour: number,
    offset: number,
    timezone: string,
  }
}

export interface IShippingMethodsResponse {
  order_ship_method: number;
  currency_code: string;
  ship_methods: IShippingMethodResponse[];
}

export interface IDeprecatedShippingMethodResponse {
  [key: string]: {
    discount: number;
    sort_order: number;
    name: string;
    price: number;
    sku_base_id: number;
  };
}

export interface IDeprecatedOrderShipAddressResponse {
  ship_methods: {
    methods: IDeprecatedShippingMethodResponse[];
  };
  currency_code: string;
  order_ship_method: string;
}

export interface ICartItemResponse {
  isKitItem: number;
  isInactive: number;
  isHazmat: number;
  itemType: string;
  kit_discounted_unit_price: number;
  kit_unit_price: number;
  isSampleItem: number;
  tax_rate: number;
  product_size: string;
  total_percent_discount: number;
  isRefillable: number;
  strength: string;
  url: string;
  shaded: number;
  sub_line: string;
  shadename: string;
  large_image?: string[];
  isOutOfStock: number;
  replenishment_item: number;
  sku_type: SKU_TYPE;
  prod_rgn_name: string;
  applied_tax: number;
  monogram_price: number;
  discounted_applied_price: number;
  discounted_unit_price: number;
  unit_price: number;
  prod_base_id: number;
  large_alt_images?: string[];
  monogrammed_item: number;
  offer_code: string;
  product_code: string;
  isSoldOut: number;
  medium_image?: string[];
  inventory_status: number;
  applied_price: number;
  sku_base_id: number;
  prod_rgn_subheading: string;
  discounted_price: number;
  item_quantity: number;
  small_image?: string[];
  prod_url: string;
  replenishment_frequency: string;
  sku_name: string;
  sku_tax_rate: string;
  thumbnail_image: string;
  hex_value: string;
  monogram_sku_price: number;
  formatted_applied_price: string;
  formatted_discounted_applied_price: string;
  formatted_discounted_unit_price: string;
  formatted_unit_price: string;
  monogram_discounted_unit_price: number;
  monogram_sku_total_percent_discount: number;
  monogram_sku_discount_price: number;
  monogram_sku_formatted_price: string;
  monogram_sku_formatted_discount_price: string;
  monogram_formatted_unit_price: string;
  monogram_total_percent_discount: number;
  monogram_unit_price: number;
  monogram_formatted_discounted_unit_price: string;
  kit_cart_quantity: number;
  kit_prod_rgn_names: string[];
  kit_formatted_unit_price: string;
  kit_formatted_discounted_unit_price: string;
  isFreeVirtualSkuItem: number;
  isPreOrder: number;
  is_bopis_gwp: number;
  is_courier_gwp: number;
  is_gwp_offer: number;
  isRewardItem: boolean;
  rewardPoints: string;
  image_s?: string[];
  image_m?: string[];
  image_l?: string[];
  estimated_delivery_date?: string;
}

export interface INotificationResponse {
  class?: string;
  code?: number;
  key: string;
  order?: string;
  severity?: string;
  text?: string;
  timestamp?: number;
  prod_rgn_name?: string;
  data?: {
    offer_code: string;
  };
}

export interface IOfferResponse {
  benefit_value?: number;
  offer_code?: string;
  description?: string;
  activation?: string;
  confirm_message?: string;
  allow_auto_messages?: number;
  benefit_class?: string;
}

export interface ITransPaymentResponse {
  balance: string;
  billing_address: IAddressResponse;
  billing_address_id: string;
  trans_payment_id: number;
  payment_amount: string;
  pp_name: string;
  payment_mode: string;
  is_ready_to_complete: number;
  gc_giftcard_number: string;
  restrict_card_number: number;
  payment_type: string;
  start_screen: string;
}

export interface IGiftCardResponse {
  trans_payments: ITransPaymentResponse[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payment_init?: IPaymentSession[] | any;
}

interface IErrorData {
  message: string;
  data: {
    products: string;
  };
}

export interface IError {
  key: string;
  data: IErrorData;
}

export interface ITransactionOrder {
  trans_id: number;
  trans_order_id: number;
  card_message: string;
  card_message_to: string;
  card_message_from: string;
  giftWrap: number;
  loyaltyPoints: number | string;
}

export interface IGiftWrapSkuItem {
  price: string;
  sku_base_id: string;
  discount_price: string;
  image: string;
  label: string;
  description: string;
}

export interface ITransactionResponse {
  cart_items: ICartItemResponse[];
  named_delivery_date_time?: {
    delivery_date?: string;
    delivery_time?: string;
  };
  gift_message: {
    enabled: number;
    char_limit?: string;
    gm_sku_price?: string;
  },
  gift_wrap: {
    default_gw_selected: number;
    enabled: number;
    gw_opted?: number;
    gw_selected_sku?: string;
    gw_sku_price?: string;
    gw_sku_discount_price?: string;
    gw_skus?: IGiftWrapSkuItem[];
    multiple_giftwraps?: number;
    hide_gw_image?: number;
    hide_shopping_bag_gw_sku?: number;
  }
  notifications: INotificationResponse[];
  order_summary: IOrderSummaryResponse;
  offers_applied: IOfferResponse[];
  user: {
    email_address?: string;
    is_guest_checkout?: number;
    accepted_privacy_policy?: number;
    addresses?: IAddressResponse[];
    shipping_addresses?: IAddressResponse[];
    user?: DeprecatedISignedInUserResponse;
    saved_cc_payments?: ICreditCardResponse[];
    pc_email_address?: string;
    recognized?: number;
    registered?: number;
    user_id?: string;
    loyalty_active_flag?: number;
    first_name?: string;
    last_name?: string;
    national_id?: number;
    pro_info?: {
      isPro: number;
    };
    iskeptsignedin?: boolean;
  };
  trans_key: string;
  trans_order: ITransactionOrder;
  trans_payments: ITransPaymentResponse[];
  payment_options: IPaymentOption[];
  order_ship_address: {
    address: IAddressResponse;
  };
  ship_methods: IShippingMethodResponse[];
  ship_options: IShipOption[];
  order_ship_method: string;
  errors: IError[];
  bopis: IBopisInfoResponse;
  convenience_store: IConvenienceStoreResponse;
  bill_to_company?: ICompanyBillingDetailsResponse;
  salon: ISalonResponse;
  installments?: IAcceptedInstallments[];
  delivery_tabs_disabled: number;
  is_POA?: number;
  delivery_options?: IDeliveryOption[];
  is_final_sale?: number;
}

export interface IEstimatedDeliveryResponse {
  cart_items: ICartItemResponse[];
  order_summary: IOrderSummaryResponse;
  ship_methods: IShippingMethodResponse[];
  ship_options: IShipOption[];
  order_ship_method: string;
  currency_code: string;
  trans_order: ITransactionOrder;
}

export interface INearbyDoorsSalon {
  zip: string;
  city: string;
  state: string;
  doorname: string;
  address: string;
}

export interface ISalonResponse {
  salon_id: string;
  stylist_name: string;
  salon_data_share_flag: boolean;
  doors: {
    [key: string]: INearbyDoorsSalon;
  };
}

export interface ISalonPatchResponse {
  errors?: IApiErrorElement[];
}

export interface ICreditCardResponse {
  billing_address_id: string;
  card_type: string;
  default_payment_method: string;
  displaycardnum: string;
  expired: number;
  expires: string;
  cc_user_payment_method_id: string;
}

export interface IAfterpayLimis {
  afterpay_limit_max: string;
  afterpay_limit_min: string;
}

export interface IPaymentOption {
  payment_name: string;
  payment_type: string;
  pp_name: string;
  client_key: string;
  qty_allowed: number;
  key: string[];
  is_available: number;
  is_default: number;
  accepted_credit_cards: IAcceptedCreditCards[];
  accepted_cash_options: IAcceptedCashOptions[];
  valid_with_giftcard: number;
  api_url: {
    sdk_js: string;
    integrity?: string;
  };
  JSURL: string;
  logo: string;
  intro_image?: string;
  payment_mode: string;
  payment_option: string;
  paypal_settings?: IPayPalSettings;
  pay_later_eligible?: number;
  profile_id?: string;
  adyen_config_obj_environment: string;
  enable_cc_save_set_default?: number;
  enable_installments?: number;
  number_of_installments?: number;
  disable_store_details?: number;
  TEST_SERVER?: number;
  has_card_holder_name?: number;
  hide_cvc?: number;
  require_card_holder_name?: number;
  grey_out_if_unavailable?: number;
  grey_out_logo?: string;
  after_pay_limits?: IAfterpayLimis;
}

export interface IAcceptedCashOptions {
  image: string;
  type: string;
  alt: string;
  value: string;
}

export interface IAcceptedCreditCards {
  image: string;
  type: string;
  alt: string;
}

export interface IShipOption {
  name: string;
  price: number;
  ship_method_id: SHIPPING_METHODS;
  sku_base_id: number;
  sort_order: number;
  next_available_sat: string;
  discount?: number;
  estimated_delivery_start_date?: string;
  estimated_delivery_end_date?: string;
}

export interface IStardustConfig {
  businessUnitId: string;
  clientId: string;
  defaultDomain: string;
  environment: string;
}

export interface IDisplayOurGuaranteeConfig {
  delivery_page: number;
  payment_page: number;
}

export interface ISiteConfig {
  region_data: {
    shipping_countries?: ICountry[];
    allowed_shipping_countries?: ICountry[];
    billing_countries?: ICountry[];
    allowed_billing_countries?: ICountry[];
    allowed_packstation_countries?: ICountry[];
    states: IStates;
    select_cities?: string[];
  };
  address_config?: {
    billing_address_input_schema: {
      properties: IAddressSchemaProperties;
    };
    shipping_address_input_schema: {
      properties: IAddressSchemaProperties;
      address: {
        properties: IAddressSchemaProperties;
      };
    };
  };
  billing_address_input_schema?: {
    properties: IAddressSchemaProperties;
  };
  shipping_address_input_schema?: {
    properties: IAddressSchemaProperties;
  };
  packstation_address_input_schema?: {
    properties: IAddressSchemaProperties;
  };
  contact_info_input_schema?: {
    properties: IAddressSchemaProperties;
  };
  bill_to_company_schema?: {
    properties: IAddressSchemaProperties;
  };
  base_url?: string;
  brand_id: number;
  brand_name: string;
  country_id?: number;
  courier?: {
    [key: string]: string;
  };
  currency?: string;
  language_id: number;
  locale: string;
  region_id: number;
  region_name: string;
  ship_options: IShipOption[];
  site_config: {
    giftwrap: number;
    display_included_tax?: number;
    country_taxes_label?: number;
    allow_gc_without_cc?: number;
    checkout_use_new_design?: number;
    checkout_idle_timeout?: number;
    display_shipping_fee_with_tax?: number;
    has_vat?: number;
    is_melissa_enabled?: number;
    is_production?: number;
    use_ups_validation_at_checkout: number;
    backend_validation?: {
      should_validate_saved_shipping_address: number;
      validation_type: BACKEND_VALIDATION_TYPES;
      country_id?: number;
    };
    use_city_state_lookup?: boolean;
    use_shipping_calculator?: boolean;
    adv_legal: number;
    tnc_content_node_id: string;
    klarna_popup_node_id: string;
    gw_default_selection: number;
    accepted_privacy_policy?: {
      message_only_terms_conditions: number;
      default_terms_conditions: number;
    };
    site: {
      country_id: number;
      currency: string;
      logo_path: string;
      paypal_path: string;
      afterpay_path: string;
    };
    show_strikeoff_price?: number;
    show_free_label_as_price?: number;
    use_json_schemas_for_address_validation?: number;
    max_gift_card_number_length?: number;
    privacy_policy_url?: string;
    google_maps_key?: string;
    use_e164_phone_standard?: number;
    enable_donation?: number;
  };
  payment_instructions: number;
  display_store_collection: number;
  rco_ups_enabled: number;
  rco_bopis_enabled: number;
  rco_bopis_gift_wrap_enabled: number;
  rco_courier_enabled: number;
  rco_salon_enabled: number;
  rco_show_courier_from_prefix: number;
  async_address: boolean;
  condensed_fields?: string[];
  rco_edd_enabled: number,
  rco_split_shipment_enabled: number,
  newsletter: boolean;
  stardust?: IStardustConfig;
  bill_to_company: boolean;
  static_lookup_urls?: {
    stateCityUrl?: string;
    stateCityLockerUrl?: string;
  };
  use_generic_address_handler?: boolean;
  display_our_guarantee_on: IDisplayOurGuaranteeConfig;
  display_store_name_with_address?: number;
  country_phone_verification_schema: IPhoneCountryPattern;
  routes: IExternalRoutes;
  google_map_style?: string;
}

export interface IShippingResponse {
  address?: IAddressAttributes;
  errors?: IError[];
}

export interface IAddressValidateResponse {
  validate?: boolean;
  errors?: IApiErrorElement[];
}

export interface IBillingAddress {
  address?: IAddressAttributes;
}

export interface ICompanyBillingDetailsResponse {
  bill_to_company: number;
  company_address: string;
  company_name: string;
  company_tax_number: string;
}

export interface ISelectShippingAddress {
  data?: {
    ship_methods: {
      methods: IShippingMethodResponse[];
    };
    currency_code: string;
    order_ship_method: string;
    order_summary: IOrderSummaryResponse;
  };
}

export interface IBillingSameAsShippingAddress {
  data?: null;
}

export interface IFindSalonsResponse {
  data: {
    doors: {
      results: {
        [key: string]: {
          ADDRESS: string;
          CITY: string;
          SALON_ID: string;
          DOORNAME: string;
          STATE_OR_PROVINCE: string;
        };
      };
    };
  };
}

export interface INotification {
  key: string;
  severity: string;
  productRgnName: string;
}

export interface IPaymentSession {
  payment_uri?: string;
  paymentSession?: string;
  redirect?: string;
  form_content?: string;
}

export interface IVerifyPayment {
  redirect?: string;
}

export interface IPrivacyPolicy {
  [key: string]: boolean;
}

export interface IShippingMethodSummary {
  order_summary: IOrderSummaryResponse;
  trans_id: number;
}

export interface ICountry {
  abbr?: string;
  default?: string;
  label: string;
  value: number;
}

// Deprecated. Remove when RCO-1901 is in production
export interface DeprecatedISignedInUserResponse {
  pc_email_address: string;
  recognized: number;
  registered: number;
  user_id: string;
}

export enum DOOR_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface IBopisResponseV2 {
  courier_door_selected?: string;
  config?: {
    uom: UNIT;
    courier_radius: number;
    bopis_radius: number;
  };
  doors?: {
    [storeId: string]: {
      info: IStoreInformation;
      door_inventory: IDoorInventory;
      store_hours: {
        raw: IStoreHours;
        rendered: IStoreWorkingTime;
      };
      omni_door_status: {
        courier_delivery: DOOR_STATUS;
      };
    };
  };
  max_radius?: string;
}

// TODO: remove deprecated V1 interface in RCO-4109
export interface IBopisResponseV1 {
  courier_door_selected?: string;
  results: {
    [storeId: string]: IStoreInformation;
  };
  store_hours: {
    [storeId: string]: {
      opening_hours: {
        raw: IStoreHours;
      };
    };
  };
  door_inventory: {
    [storeId: string]: IDoorInventory;
  };
}

export interface IStoreInformation {
  ADDRESS: string;
  CITY: string;
  COUNTRY: string;
  DISTANCE: number;
  DOOR_ID: string;
  DOORNAME: string;
  LATITUDE: number;
  LONGITUDE: number;
  PHONE1: string;
  ZIP_OR_POSTAL: string;
  STATE: string;
  SUB_HEADING: string;
  TIME_ZONE: string;
  TIME_ZONE_UTC_OFFSET: string;
}

interface IDoorInventory {
  skus_onhand: {
    [skuId: string]: {
      available_qty: number;
      is_available: number;
      is_free_item: number;
      is_gwp_offer: number;
      is_sample_item: number;
      gwp_saleable_sku: string;
    };
  };
}

export interface IStoreHours {
  [dayNum: number]: {
    close: string;
    open: string;
  };
}

export interface IStoreWorkingTime {
  [dayNum: number]: string;
}

export interface IBopisInfoResponse {
  bopis?: IBopisInfoResponse;
  notifications?: INotificationResponse[];
  bopis_is_bopis_or_courier: number;
  bopis_is_bopis_order: number;
  bopis_is_courier: number;
  bopis_purchaser_pickup_info: {
    bopis_email_address: string;
    bopis_first_name: string;
    bopis_last_name: string;
    bopis_phone_number: string;
  };
  bopis_proxy_pickup_info: {
    proxy_email_address: string;
    proxy_first_name: string;
    proxy_last_name: string;
    proxy_phone: string;
  };
  bopis_store_number: string;
}

export interface ISetTransactionKeyResponse {
  validated_trans: boolean;
}

export interface IGetTransactionKeyResponse {
  trans_key: string;
}

export interface IMunicipalityColonies {
  municipality: string;
  colonies: string[];
}

export interface IAcceptedInstallments {
  value: string;
  label: string;
}

export interface IConvenienceStoreResponse {
  address: string;
  name: string;
  phone: string;
  postcode: string;
  type?: CONVENIENCE_STORE;
}

export interface IGoogleLocation {
  location: {
    latitude: () => number;
    longitude: () => number;
  };
}

interface IMercadoPagoApiResponseSetting {
  card_number: {
    validation: string;
    length: number;
  };
  bin: {
    pattern: string;
    installments_pattern: string;
    exclusion_pattern: string;
  };
  security_code: {
    length: number;
    card_location: string;
    mode: string;
  };
}

export interface IPSEPaymentMethod {
  id: string;
  description: string;
}

export interface IMercadoPagoApiResponse {
  id: string;
  name: string;
  payment_type_id: string;
  status: string;
  secure_thumbnail: string;
  thumbnail: string;
  deferred_capture: string;
  settings: IMercadoPagoApiResponseSetting[];
  additional_info_needed: string[];
  min_allowed_amount: number;
  max_allowed_amount: number;
  accreditation_time?: number;
  financial_institutions: IPSEPaymentMethod[];
  processing_modes: string[];
}

export interface IHtmlContentResponse {
  data: {
    [key: number]: {
      title: string;
      language: string;
      field_coremetrics_title: {
        value: string;
        format: null | string;
        safe_value: string;
        language: string;
      };
      seo_canonical: string;
      seo_title: string;
      template_fields: {
        data: {
          html: {
            textarea: {
              format: string;
              value: string;
            };
          };
        };
      };
      attached: {
        css: string[];
        js: string[];
      };
      rendered: string;
    };
  }
}